import React, {useState, useEffect, useRef, MouseEvent, useImperativeHandle} from 'react';
import {Span} from './TraceDetails';
import {Link as LinkType} from '../../../pages/Traces';
import {
    ZoomInIcon,
    ZoomOutIcon,
    RefreshCwIcon,
    XIcon,
    TagIcon,
    InfoIcon,
    ExternalLinkIcon,
    Link,
    SearchIcon,
    FileTextIcon
} from 'lucide-react';
import TagContainer from '../Tags/TagContainer';
import {LogsComponent} from '../../../pages/Logs';
import {Transform} from '../../../components/Dashboarding/widgets/TransformSelector';

interface SpanNode {
    span: Span;
    children: SpanNode[];
    level: number;
    startTime: number; // milliseconds
    endTime: number; // milliseconds
    collapsed: boolean;
}

export interface TraceFlameGraphHandle {
    openTraceLogs: () => void;
}

interface TraceFlameGraphProps {
    spans: Span[];
    filter?: Map<string, string[]>;
    setFilter?: (filter: Map<string, string[]>) => void;
    excludeFilter?: Map<string, string[]>;
    setExcludeFilter?: (filter: Map<string, string[]>) => void;
    onTreeBuildFailed?: () => void;
    onTreeBuildSuccess?: () => void;
    highlightSpanId?: string;
    linkedTraceId?: string;
    traceId: string;
}

// Constants for time unit conversions
const NANOS_TO_MILLIS = 1000000;

// Constants for UI
const MIN_DIRECTORY_WIDTH = 200; // Minimum width of directory in pixels
const MAX_DIRECTORY_WIDTH = 600; // Maximum width of directory in pixels
const DEFAULT_DIRECTORY_WIDTH = 320; // Default width of directory in pixels
const MIN_DETAILS_HEIGHT = 100; // Minimum height of details panel in pixels
const MAX_DETAILS_HEIGHT = 800; // Maximum height of details panel in pixels
const DEFAULT_DETAILS_HEIGHT = 500; // Default height of details panel in pixels (40% is roughly this)

export const TraceFlameGraph = React.forwardRef<TraceFlameGraphHandle, TraceFlameGraphProps>(({
                                                                    spans,
                                                                    filter = new Map<string, string[]>(),
                                                                    setFilter = () => {
                                                                    },
                                                                    excludeFilter = new Map<string, string[]>(),
                                                                    setExcludeFilter = () => {
                                                                    },
                                                                    onTreeBuildFailed = () => {
                                                                    },
                                                                    onTreeBuildSuccess = () => {
                                                                    },
                                                                    highlightSpanId,
                                                                    linkedTraceId,
                                                                    traceId
                                                                }, ref) => {
    const [spanTree, setSpanTree] = useState<SpanNode | null>(null);
    const [timelineRange, setTimelineRange] = useState<{ start: number; end: number }>({start: 0, end: 0});
    const [originalTimeRange, setOriginalTimeRange] = useState<{ start: number; end: number }>({start: 0, end: 0});
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [containerHeight, setContainerHeight] = useState<number>(0);
    const [hoveredSpan, setHoveredSpan] = useState<SpanNode | null>(null);
    const [selectedSpan, setSelectedSpan] = useState<SpanNode | null>(null);
    const [zoomLevel, setZoomLevel] = useState<number>(1);
    const [showDetailPanel, setShowDetailPanel] = useState<boolean>(false);
    const [directoryWidth, setDirectoryWidth] = useState<number>(DEFAULT_DIRECTORY_WIDTH);
    const [detailsHeight, setDetailsHeight] = useState<number>(DEFAULT_DETAILS_HEIGHT);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [isResizingDetails, setIsResizingDetails] = useState<boolean>(false);
    const lastMousePosRef = useRef<number>(0);
    const lastMouseYPosRef = useRef<number>(0);
    const [isPanning, setIsPanning] = useState<boolean>(false);
    const lastPanPosRef = useRef<number>(0);

    // State to store missing parent span ID
    const [missingParentSpanId, setMissingParentSpanId] = useState<string | null>(null);
    // State to control keyboard shortcut tooltip visibility
    const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState<boolean>(true);
    
    // Search functionality state
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [matchingSpanIds, setMatchingSpanIds] = useState<Set<string>>(new Set());
    const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
    
    // Tab state for the details panel
    const [activeTab, setActiveTab] = useState<'details' | 'logs'>('details');
    
    // State for logs integration in the tab
    const [logsFilter, setLogsFilter] = useState<Map<string, string[]>>(new Map());
    const [logsExcludeFilter, setLogsExcludeFilter] = useState<Map<string, string[]>>(new Map());
    const [logsRegexes, setLogsRegexes] = useState<string[]>([]);
    const [logsExcludeRegexes, setLogsExcludeRegexes] = useState<string[]>([]);
    const [logsAscending, setLogsAscending] = useState<boolean>(false);
    const [logsTransforms, setLogsTransforms] = useState<Transform[]>([]);
    const [logsCustomColumns, setLogsCustomColumns] = useState<string[]>([]);
    const [isViewingTraceLogs, setIsViewingTraceLogs] = useState<boolean>(false);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        openTraceLogs: () => {
            // Find the root span and select it
            if (spanTree) {
                setIsViewingTraceLogs(true);
                setSelectedSpan(spanTree);
                setShowDetailPanel(true);
                setActiveTab('logs');
                // Set filter to traceId instead of spanId
                const newFilter = new Map<string, string[]>();
                const traceIdForLogs = linkedTraceId || traceId;
                newFilter.set("traceId", [traceIdForLogs]);
                setLogsFilter(newFilter);
            }
        }
    }), [spanTree, linkedTraceId, traceId]);

    // Build the span tree from the flat list of spans
    useEffect(() => {
        if (!spans || spans.length === 0) return;

        // Reset missing parent span ID
        setMissingParentSpanId(null);

        // Find trace start and end times
        // time is in milliseconds, duration is in nanoseconds
        let minTime = Number.MAX_SAFE_INTEGER;
        let maxTime = 0;

        spans.forEach(span => {
            minTime = Math.min(minTime, span.time);
            // Convert duration from nanos to millis for consistent time handling
            maxTime = Math.max(maxTime, span.time + (span.duration / NANOS_TO_MILLIS));
        });

        // Use the actual trace time range without enforcing a minimum
        setTimelineRange({start: minTime, end: maxTime});
        setOriginalTimeRange({start: minTime, end: maxTime});

        // Force reset zoom level when loading new spans
        setZoomLevel(1);

        // Create a map of spans by ID for quick lookup
        const spanMap = new Map<string, Span>();
        spans.forEach(span => {
            spanMap.set(span.spanId, span);
        });

        // Find the root span (has no parent or parent is "0000000000000000")
        let rootSpan = spans.find(span =>
            !span.parentSpanId || span.parentSpanId === "0000000000000000"
        );

        // If no root span is found, look for spans with parentSpanId that doesn't exist in the spans array
        if (!rootSpan) {
            // Find spans with parentSpanId that doesn't exist in the spans array
            const orphanSpans = spans.filter(span =>
                span.parentSpanId &&
                span.parentSpanId !== "0000000000000000" &&
                !spanMap.has(span.parentSpanId)
            );

            if (orphanSpans.length > 0) {
                // Use the first orphan span as the root span
                rootSpan = orphanSpans[0];
                // Store the missing parent span ID
                setMissingParentSpanId(rootSpan.parentSpanId);
            } else {
                // Call the onTreeBuildFailed callback if no root span is found
                onTreeBuildFailed();
                return;
            }
        }

        // Recursively build the tree
        const buildSpanTree = (span: Span, level: number): SpanNode => {
            const children: SpanNode[] = [];

            // Find all spans that have this span as parent
            spans.forEach(childSpan => {
                if (childSpan.parentSpanId === span.spanId) {
                    children.push(buildSpanTree(childSpan, level + 1));
                }
            });

            // Sort children by start time
            children.sort((a, b) => a.span.time - b.span.time);

            return {
                span,
                children,
                level,
                startTime: span.time, // milliseconds
                endTime: span.time + (span.duration / NANOS_TO_MILLIS), // convert duration to milliseconds
                collapsed: false
            };
        };

        const root = buildSpanTree(rootSpan, 0);
        setSpanTree(root);

        // Don't clear selected span when spans haven't actually changed
        // This prevents the detail panel from flickering when parent component re-renders

        // If there's only a single span, automatically select it and show details panel
        if (spans.length === 1) {
            const spanNode = root;
            setSelectedSpan(spanNode);
            setShowDetailPanel(true);
        }

        // Call the onTreeBuildSuccess callback when the tree is successfully built
        onTreeBuildSuccess();
    }, [spans, onTreeBuildFailed, onTreeBuildSuccess]);

    // Update logs filter when selected span changes
    useEffect(() => {
        // Don't update filter if we're viewing trace logs (not span logs)
        if (isViewingTraceLogs) {
            return;
        }
        
        const newFilter = new Map<string, string[]>();
        if (selectedSpan) {
            newFilter.set("spanId", [selectedSpan.span.spanId]);
        } else {
            // Use OTel traceId if linked, otherwise use eBPF traceId
            const traceIdForLogs = linkedTraceId || traceId;
            newFilter.set("traceId", [traceIdForLogs]);
        }
        setLogsFilter(newFilter);
    }, [selectedSpan, linkedTraceId, traceId, isViewingTraceLogs]);

    // Handle highlighting the specified span
    useEffect(() => {
        if (highlightSpanId && spanTree) {
            // Helper function to find a node by spanId
            const findNodeByIdLocal = (node: SpanNode, spanId: string): SpanNode | null => {
                if (node.span.spanId === spanId) return node;

                for (const child of node.children) {
                    const found = findNodeByIdLocal(child, spanId);
                    if (found) return found;
                }

                return null;
            };

            const nodeToHighlight = findNodeByIdLocal(spanTree, highlightSpanId);
            if (nodeToHighlight) {
                setSelectedSpan(nodeToHighlight);
                setShowDetailPanel(true);
                // Zoom to the highlighted span after a short delay to ensure layout is ready
                setTimeout(() => {
                    zoomToSpan(nodeToHighlight);
                }, 100);
            }
        }
    }, [highlightSpanId, spanTree]);

    // Search functionality - find matching spans based on search query
    useEffect(() => {
        if (!searchQuery.trim() || !spans) {
            setMatchingSpanIds(new Set());
            setIsSearchActive(false);
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const matches = new Set<string>();

        spans.forEach(span => {
            // Check if spanId matches
            if (span.spanId.toLowerCase().includes(query)) {
                matches.add(span.spanId);
                return;
            }

            // Check if span name matches
            if (span.spanName && span.spanName.toLowerCase().includes(query)) {
                matches.add(span.spanId);
                return;
            }

            // Check span attributes
            if (span.spanAttributes) {
                for (const [key, value] of Object.entries(span.spanAttributes)) {
                    if (value && value.toString().toLowerCase().includes(query)) {
                        matches.add(span.spanId);
                        return;
                    }
                }
            }

            // Check resource attributes
            if (span.resourceAttributes) {
                for (const [key, value] of Object.entries(span.resourceAttributes)) {
                    if (value && value.toString().toLowerCase().includes(query)) {
                        matches.add(span.spanId);
                        return;
                    }
                }
            }
        });

        setMatchingSpanIds(matches);
        setIsSearchActive(matches.size > 0);
        
        // Auto-select first match if there are matches
        if (matches.size > 0 && spanTree) {
            const firstMatchId = Array.from(matches)[0];
            const findNodeByIdLocal = (node: SpanNode, spanId: string): SpanNode | null => {
                if (node.span.spanId === spanId) return node;
                for (const child of node.children) {
                    const found = findNodeByIdLocal(child, spanId);
                    if (found) return found;
                }
                return null;
            };
            
            const firstMatchNode = findNodeByIdLocal(spanTree, firstMatchId);
            if (firstMatchNode && (!selectedSpan || selectedSpan.span.spanId !== firstMatchId)) {
                setSelectedSpan(firstMatchNode);
                setShowDetailPanel(true);
                setTimeout(() => {
                    zoomToSpan(firstMatchNode);
                }, 100);
            }
        }
    }, [searchQuery, spans, spanTree]);

    // Update container width and height on mount and resize
    useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth - 20);
                setContainerHeight(containerRef.current.offsetHeight);
            }
        };

        // Call initially
        updateDimensions();

        // Use ResizeObserver for more reliable size tracking
        const resizeObserver = new ResizeObserver(() => {
            updateDimensions();
        });

        resizeObserver.observe(containerRef.current);

        // Fallback to window resize event
        window.addEventListener('resize', updateDimensions);

        // Force a recalculation after a short delay to handle initial render issues
        const timeoutId = setTimeout(updateDimensions, 100);

        return () => {
            if (containerRef.current) {
                resizeObserver.unobserve(containerRef.current);
            }
            window.removeEventListener('resize', updateDimensions);
            clearTimeout(timeoutId);
        };
    }, [containerRef.current, spans]);

    // Add effect to recalculate when directoryWidth or detailsHeight changes
    useEffect(() => {
        const updateLayout = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
                setContainerHeight(containerRef.current.offsetHeight);
            }
        };
        updateLayout();
    }, [directoryWidth, detailsHeight]);

    // Toggle collapse state of a span
    const toggleCollapse = (node: SpanNode) => {
        setSpanTree(prevTree => {
            if (!prevTree) return null;

            // Create a new tree with the updated collapsed state
            const updateNode = (currentNode: SpanNode): SpanNode => {
                if (currentNode.span.spanId === node.span.spanId) {
                    return {...currentNode, collapsed: !currentNode.collapsed};
                }

                return {
                    ...currentNode,
                    children: currentNode.children.map(updateNode)
                };
            };

            return updateNode(prevTree);
        });
    };

    // Handle zoom in
    const handleZoomIn = () => {
        // Zoom in centered on the middle of the timeline
        const currentRange = timelineRange.end - timelineRange.start;
        const zoomFactor = 0.85; // Zoom in by 15% (smaller increment)
        const newRange = currentRange * zoomFactor;
        const rangeChange = newRange - currentRange;

        // Use the middle of the timeline as the zoom point
        const midpoint = timelineRange.start + (currentRange / 2);
        const mouseOffset = 0.5; // Middle point

        const startDelta = rangeChange * mouseOffset;
        const endDelta = rangeChange * (1 - mouseOffset);

        setZoomLevel(zoomLevel * 1.15); // Smaller zoom level adjustment
        setTimelineRange({
            start: Math.max(timelineRange.start - startDelta, originalTimeRange.start),
            end: Math.min(timelineRange.end + endDelta, originalTimeRange.end)
        });
    };

    // Handle zoom out
    const handleZoomOut = () => {
        // Zoom out centered on the middle of the timeline
        const currentRange = timelineRange.end - timelineRange.start;
        const zoomFactor = 1.15; // Zoom out by 15% (smaller increment)

        // Don't zoom out beyond the original range, but allow zooming out if we're zoomed in too much
        const newRange = currentRange * zoomFactor;
        const originalRange = originalTimeRange.end - originalTimeRange.start;
        if (newRange > originalRange * 1.5 && currentRange >= originalRange * 0.5) return;

        const rangeChange = newRange - currentRange;

        // Use the middle of the timeline as the zoom point
        const midpoint = timelineRange.start + (currentRange / 2);
        const mouseOffset = 0.5; // Middle point

        const startDelta = rangeChange * mouseOffset;
        const endDelta = rangeChange * (1 - mouseOffset);

        // Apply the new time range
        const newStart = Math.max(timelineRange.start - startDelta, originalTimeRange.start);
        const newEnd = Math.min(timelineRange.end + endDelta, originalTimeRange.end);

        // Ensure we're not zooming to an impossibly small range
        if (newEnd - newStart < 0.001) {
            return;
        }

        setZoomLevel(zoomLevel / 1.15); // Smaller zoom level adjustment
        setTimelineRange({
            start: newStart,
            end: newEnd
        });
    };

    // Handle reset zoom
    const handleResetZoom = () => {
        setZoomLevel(1);
        setTimelineRange(originalTimeRange);
    };

    // Function to get the time range that encompasses a span and all its descendants
    const getSpanAndDescendantsTimeRange = (node: SpanNode): { start: number; end: number } => {
        let minTime = node.startTime;
        let maxTime = node.endTime;

        const traverseChildren = (currentNode: SpanNode) => {
            minTime = Math.min(minTime, currentNode.startTime);
            maxTime = Math.max(maxTime, currentNode.endTime);

            currentNode.children.forEach(child => {
                traverseChildren(child);
            });
        };

        traverseChildren(node);

        return {start: minTime, end: maxTime};
    };

    // Function to zoom into a specific span and its descendants
    const zoomToSpan = (node: SpanNode) => {
        const spanRange = getSpanAndDescendantsTimeRange(node);
        const duration = spanRange.end - spanRange.start;

        // Add 20% padding on each side, but ensure we don't go outside the original range
        const padding = Math.max(duration * 0.2, 1); // Minimum 1ms padding
        const newStart = Math.max(spanRange.start - padding, originalTimeRange.start);
        const newEnd = Math.min(spanRange.end + padding, originalTimeRange.end);

        // Ensure minimum zoom range
        const minRange = (originalTimeRange.end - originalTimeRange.start) * 0.001; // 0.1% of original range
        if (newEnd - newStart < minRange) {
            const center = (newStart + newEnd) / 2;
            const halfRange = minRange / 2;
            setTimelineRange({
                start: Math.max(center - halfRange, originalTimeRange.start),
                end: Math.min(center + halfRange, originalTimeRange.end)
            });
        } else {
            setTimelineRange({start: newStart, end: newEnd});
        }

        // Update zoom level for UI consistency
        const originalDuration = originalTimeRange.end - originalTimeRange.start;
        const newDuration = newEnd - newStart;
        setZoomLevel(originalDuration / newDuration);
    };

    // Handle mouse down on resize handle
    const handleResizeStart = (e: MouseEvent) => {
        setIsResizing(true);
        lastMousePosRef.current = e.clientX;
        e.preventDefault();
    };

    // Handle mouse move for resizing
    useEffect(() => {
        if (!isResizing) return;

        const handleResize = (e: MouseEvent) => {
            const delta = e.clientX - lastMousePosRef.current;
            lastMousePosRef.current = e.clientX;

            setDirectoryWidth((prevWidth) => {
                // Keep the width between min and max constraints
                const newWidth = Math.max(MIN_DIRECTORY_WIDTH, Math.min(MAX_DIRECTORY_WIDTH, prevWidth + delta));
                return newWidth;
            });

            // Force immediate layout update
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        const handleResizeEnd = () => {
            setIsResizing(false);
        };

        // Use MouseEvent to ensure proper typing
        const handleMouseMove = (e: MouseEvent) => {
            handleResize(e as MouseEvent);
            // Prevent text selection during resize
            e.preventDefault();
        };

        document.addEventListener('mousemove', handleMouseMove as unknown as EventListener);
        document.addEventListener('mouseup', handleResizeEnd);

        // Add class to body to indicate resizing is in progress
        document.body.style.cursor = 'col-resize';
        document.body.classList.add('select-none');

        return () => {
            document.removeEventListener('mousemove', handleMouseMove as unknown as EventListener);
            document.removeEventListener('mouseup', handleResizeEnd);
            // Remove resizing indicator when done
            document.body.style.cursor = '';
            document.body.classList.remove('select-none');
        };
    }, [isResizing]);

    // Handle mouse down on details resize handle
    const handleDetailsResizeStart = (e: MouseEvent) => {
        setIsResizingDetails(true);
        lastMouseYPosRef.current = e.clientY;
        e.preventDefault();
    };

    // Handle mouse move for details resizing
    useEffect(() => {
        if (!isResizingDetails) return;

        const handleResize = (e: MouseEvent) => {
            const delta = lastMouseYPosRef.current - e.clientY;
            lastMouseYPosRef.current = e.clientY;

            setDetailsHeight((prevHeight) => {
                // Keep the height between min and max constraints
                const newHeight = Math.max(MIN_DETAILS_HEIGHT, Math.min(MAX_DETAILS_HEIGHT, prevHeight + delta));
                return newHeight;
            });

            // Force immediate layout update
            if (containerRef.current) {
                setContainerHeight(containerRef.current.offsetHeight);
            }
        };

        const handleResizeEnd = () => {
            setIsResizingDetails(false);
        };

        // Use MouseEvent to ensure proper typing
        const handleMouseMove = (e: MouseEvent) => {
            handleResize(e as MouseEvent);
            // Prevent text selection during resize
            e.preventDefault();
        };

        document.addEventListener('mousemove', handleMouseMove as unknown as EventListener);
        document.addEventListener('mouseup', handleResizeEnd);

        // Add class to body to indicate resizing is in progress
        document.body.style.cursor = 'row-resize';
        document.body.classList.add('select-none');

        return () => {
            document.removeEventListener('mousemove', handleMouseMove as unknown as EventListener);
            document.removeEventListener('mouseup', handleResizeEnd);
            // Remove resizing indicator when done
            document.body.style.cursor = '';
            document.body.classList.remove('select-none');
        };
    }, [isResizingDetails]);

    // Calculate position and width for a span bar
    const calculateSpanPosition = (node: SpanNode) => {
        if (containerWidth <= directoryWidth) return {left: directoryWidth, width: 10}; // Fallback for extremely small containers

        const timeRange = timelineRange.end - timelineRange.start;
        if (timeRange === 0) return {left: directoryWidth, width: 10}; // Prevent division by zero

        const timelineWidth = Math.max(containerWidth - directoryWidth, 50); // Ensure at least some width for timeline

        let startOffset = ((node.startTime - timelineRange.start) / timeRange) * timelineWidth;
        let duration = ((node.endTime - node.startTime) / timeRange) * timelineWidth;

        // Ensure spans that start before visible range are clipped properly
        if (node.startTime < timelineRange.start) {
            duration -= (timelineRange.start - node.startTime) / timeRange * timelineWidth;
            startOffset = 0;
        }

        // Ensure spans that end after visible range are clipped properly
        if (node.endTime > timelineRange.end) {
            duration = ((timelineRange.end - Math.max(node.startTime, timelineRange.start)) / timeRange) * timelineWidth;
        }

        return {
            left: startOffset + directoryWidth, // Add directory width
            width: Math.max(duration, 5) // Ensure minimum width for visibility
        };
    };

    // Format time duration in a human-readable format
    const formatDuration = (nanoseconds: number) => {
        if (nanoseconds < 1000) {
            return `${nanoseconds.toFixed(0)}ns`;
        } else if (nanoseconds < 1000000) {
            return `${(nanoseconds / 1000).toFixed(2)}µs`;
        } else if (nanoseconds < 1000000000) {
            return `${(nanoseconds / 1000000).toFixed(2)}ms`;
        } else {
            return `${(nanoseconds / 1000000000).toFixed(2)}s`;
        }
    };

    // Format absolute time
    const formatTime = (time: number) => {
        return new Date(time).toISOString().substring(11, 23);
    };

    // Format relative time (from trace start)
    const formatRelativeTime = (timeMs: number) => {
        const relativeTimeMs = timeMs - timelineRange.start;

        if (relativeTimeMs < 0.001) {
            return "0ms";
        } else if (relativeTimeMs < 1) {
            return `${(relativeTimeMs * 1000).toFixed(2)}μs`;
        } else if (relativeTimeMs < 1000) {
            return `${relativeTimeMs.toFixed(2)}ms`;
        } else {
            return `${(relativeTimeMs / 1000).toFixed(2)}s`;
        }
    };

    // Get percentage of time elapsed
    const getTimePercentage = (time: number) => {
        const totalRange = timelineRange.end - timelineRange.start;
        if (totalRange === 0) return 0;
        return ((time - timelineRange.start) / totalRange) * 100;
    };

    // Generate timeline markers
    const renderTimelineMarkers = () => {
        if (!timelineRange.start || !timelineRange.end) return null;

        const timeRange = timelineRange.end - timelineRange.start;
        if (timeRange === 0) return null; // Prevent division by zero

        if (containerWidth <= directoryWidth) {
            // Container is too small for meaningful timeline
            return (
                <div className="h-8 flex items-center justify-center">
                    <div className="text-xs text-textmedium">Trace Timeline</div>
                </div>
            );
        }

        const totalDuration = formatDuration(timeRange * NANOS_TO_MILLIS);
        // Adjust marker count based on available width
        const availableWidth = containerWidth - directoryWidth;
        const markerCount = Math.max(3, Math.min(6, Math.floor(availableWidth / 100)));

        return (
            <div className="h-8 flex items-center relative bg-backgroundlight/30">
                {/* Time markers */}
                <div className="absolute left-4 inset-0">
                    <div className="relative h-full w-full">
                        {/* Marker lines */}
                        {Array.from({length: markerCount + 1}).map((_, i) => {
                            const percent = (i / markerCount) * 97;
                            return (
                                <div
                                    key={i}
                                    className="absolute bottom-0 w-px h-2 bg-backgroundlight/50"
                                    style={{left: `${percent}%`}}
                                />
                            );
                        })}

                        {/* Time labels */}
                        {Array.from({length: markerCount + 1}).map((_, i) => {
                            const percent = (i / markerCount) * 97;
                            const time = timelineRange.start + (timeRange * i / markerCount);
                            return (
                                <div
                                    key={i}
                                    className="absolute text-xs text-textmedium"
                                    style={{
                                        left: `${percent}%`,
                                        bottom: '4px',
                                        transform: 'translateX(-50%)'
                                    }}
                                >
                                    {formatRelativeTime(time)}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Helper function to count total visible descendants of a node
    const countVisibleDescendants = (node: SpanNode): number => {
        if (!node || node.collapsed) return 0;

        let count = node.children.length;
        for (const child of node.children) {
            count += countVisibleDescendants(child);
        }
        return count;
    };

    // Helper function to get the total height needed for direct children only
    const getChildrenHeight = (node: SpanNode): number => {
        if (!node || node.collapsed || node.children.length === 0) return 0;

        // Each child row is 36px high
        // For the last child, we don't need to extend the line beyond its position
        // For non-last children, we need to include their descendants too
        let height = 0;

        node.children.forEach((child, index) => {
            // Add height for this child's row
            height += 36;

            // If this is not the last child and it has its own children, add height for those too
            if (index < node.children.length - 1 && child.children.length > 0 && !child.collapsed) {
                height += getChildrenHeight(child);
            }
        });

        return height;
    };

    // Helper function to determine if a span is in the ancestry path of the selected span
    const isInSelectedPath = (node: SpanNode): boolean => {
        if (!selectedSpan) return false;

        // If this node is the selected span, it's in the path
        if (node.span.spanId === selectedSpan.span.spanId) return true;

        // Check if this node is an ancestor of the selected span
        // by traversing up from the selected span to the root
        let currentSpan = selectedSpan;
        while (currentSpan.span.parentSpanId && currentSpan.span.parentSpanId !== "0000000000000000") {
            // If the current parent ID matches this node's ID, it's in the path
            if (currentSpan.span.parentSpanId === node.span.spanId) return true;

            // Move up to the parent span
            const parentSpan = spanTree ? findNodeById(spanTree, currentSpan.span.parentSpanId) : null;
            if (!parentSpan) break;
            currentSpan = parentSpan;
        }

        return false;
    };

    // Helper function to determine if this node is on the path TO the selected span
    // But not AFTER the selected span (not its children)
    const isInPathToSelected = (node: SpanNode): boolean => {
        const inPath = isInSelectedPath(node);

        // If it's not in the path at all, return false
        if (!inPath) return false;

        // If this IS the selected span, return true
        if (node.span.spanId === selectedSpan?.span.spanId) return true;

        // If the selected span is a descendant of this node, return true
        // (we're on the path TO the selected span)
        return isAncestorOf(node, selectedSpan);
    };

    // Helper function to check if a node is an ancestor of another node
    const isAncestorOf = (potentialAncestor: SpanNode, potentialDescendant: SpanNode | null): boolean => {
        if (!potentialDescendant) return false;

        // If the potential descendant's parent is this node, it's an ancestor
        if (potentialDescendant.span.parentSpanId === potentialAncestor.span.spanId) return true;

        // Recursively check up the chain
        let currentNode = potentialDescendant;
        while (currentNode.span.parentSpanId && currentNode.span.parentSpanId !== "0000000000000000") {
            // If this ancestor ID matches the potential ancestor's ID, it's an ancestor
            if (currentNode.span.parentSpanId === potentialAncestor.span.spanId) return true;

            // Move up to the parent
            const parentNode = spanTree ? findNodeById(spanTree, currentNode.span.parentSpanId) : null;
            if (!parentNode) break;
            currentNode = parentNode;
        }

        return false;
    };

    // Helper function to find a node by spanId
    const findNodeById = (node: SpanNode, spanId: string): SpanNode | null => {
        if (node.span.spanId === spanId) return node;

        for (const child of node.children) {
            const found = findNodeById(child, spanId);
            if (found) return found;
        }

        return null;
    };

    // Helper function to get the appropriate color for spans and connecting lines
    const getPathStyles = (node: SpanNode) => {
        // This is the selected span
        if (selectedSpan && node.span.spanId === selectedSpan.span.spanId) {
            return {
                text: 'text-primary',
                border: 'border-primary',
                bg: 'bg-primary',
                lineBg: 'bg-primary'
            };
        }

        // This is a parent/ancestor of the selected span
        if (isInPathToSelected(node)) {
            return {
                text: 'text-primarytransparentless',
                border: 'border-primarytransparentless',
                bg: 'bg-primarytransparentless',
                lineBg: 'bg-primarytransparentless'
            };
        }

        // Default - not in the selected path
        return {
            text: 'text-textmedium',
            border: 'border-gray-400',
            bg: 'bg-gray-100/10',
            lineBg: 'bg-gray-400'
        };
    };

    // Helper function to check if a node or any of its descendants match the search
    const isNodeOrDescendantMatching = (node: SpanNode): boolean => {
        if (!isSearchActive) return true;
        
        // Check if current node matches
        if (matchingSpanIds.has(node.span.spanId)) return true;
        
        // Check if any descendant matches
        for (const child of node.children) {
            if (isNodeOrDescendantMatching(child)) return true;
        }
        
        return false;
    };

    // Render span tree with improved parallel span handling
    const renderSpanTree = (node: SpanNode, isLast = false): React.ReactNode => {
        if (!node) return null;

        // Check if this node or any of its descendants match the search
        if (!isNodeOrDescendantMatching(node)) {
            return null;
        }

        const spanPosition = calculateSpanPosition(node);
        const childrenVisible = !node.collapsed;

        // Skip spans that are completely outside the visible range
        if (node.endTime < timelineRange.start || node.startTime > timelineRange.end) {
            // Still render children if they might be visible
            return childrenVisible ? (
                <React.Fragment>
                    {node.children.map((childNode, index) =>
                        renderSpanTree(childNode, index === node.children.length - 1)
                    )}
                </React.Fragment>
            ) : null;
        }

        // Calculate total descendants to determine vertical line height
        const totalDescendants = countVisibleDescendants(node);

        // Determine status color
        let statusColor = 'bg-blue-500';
        let statusBorder = 'border-blue-600';
        let hoverBg = 'hover:bg-blue-600/10';

        if (node.span.statusCode === 'STATUS_CODE_ERROR') {
            statusColor = 'bg-red-500';
            statusBorder = 'border-red-600';
            hoverBg = 'hover:bg-red-600/10';
        } else if (node.span.statusCode === 'STATUS_CODE_OK') {
            statusColor = 'bg-green-500';
            statusBorder = 'border-green-600';
            hoverBg = 'hover:bg-green-600/10';
        }

        // Check if this span is selected or in the ancestry path of the selected span
        const isSelected = selectedSpan && selectedSpan.span.spanId === node.span.spanId;
        const inSelectedPath = isInPathToSelected(node);
        const isMatching = isSearchActive && matchingSpanIds.has(node.span.spanId);
        const rowBg = isMatching ? 'bg-yellow-500/10' : isSelected ? 'bg-primary/10' : '';

        // Get appropriate styles for path highlighting
        const pathStyles = getPathStyles(node);

        // Calculate the correct height for the connecting line
        const connectingLineHeight = getChildrenHeight(node);

        return (
            <div key={node.span.spanId} className="flex flex-col">
                {/* Current span row */}
                <div
                    className={`flex items-center h-9 ${rowBg} ${hoverBg} relative`}
                    onClick={() => {
                        setIsViewingTraceLogs(false);
                        setSelectedSpan(node);
                        setShowDetailPanel(true);
                    }}
                >
                    {/* Tree view with indentation - fixed width, no overflow */}
                    <div
                        className="flex-shrink-0 flex items-center cursor-pointer overflow-hidden border-r border-backgroundlight/30"
                        style={{width: `${directoryWidth}px`}}>
                        <div className="flex items-center h-full overflow-hidden"
                             style={{paddingLeft: `${node.level * 20}px`}}>
                            {/* Toggle button or leaf node indicator */}
                            <div className="relative mr-2 flex-shrink-0">
                                {/* Horizontal line to node */}
                                <div className={`absolute top-1/2 -left-2 w-2 h-px ${
                                    pathStyles.lineBg
                                }`}></div>

                                {/* Vertical line down from this node to its children */}
                                {node.children.length > 0 && !node.collapsed && (
                                    <div className={`absolute left-1/2 top-1/2 bottom-0 w-px ${
                                        // Only color the vertical line if this node is selected 
                                        // or if any of its children is on the path to the selected node
                                        (isSelected || (inSelectedPath && node.children.some(child => isInPathToSelected(child))))
                                            ? pathStyles.lineBg
                                            : 'bg-gray-400'
                                    }`}
                                         style={{height: '100%', transform: 'translateX(-50%)'}}></div>
                                )}

                                {/* Toggle button or leaf indicator */}
                                {node.children.length > 0 ? (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleCollapse(node);
                                        }}
                                        className={`w-4 h-4 flex items-center justify-center text-xs border rounded-sm ${
                                            inSelectedPath
                                                ? `${pathStyles.text} ${pathStyles.border}`
                                                : 'text-textmedium border-gray-400'
                                        } bg-backgrounddark z-10 relative hover:text-primary hover:border-primary`}
                                    >
                                        {node.collapsed ? '+' : '−'}
                                    </button>
                                ) : (
                                    <div className={`w-3 h-3 rounded-full flex items-center justify-center ${
                                        inSelectedPath ? 'bg-primary/10' : 'bg-gray-100/10'
                                    }`}>
                                        <div className={`w-1.5 h-1.5 rounded-full ${
                                            inSelectedPath ? pathStyles.bg : 'bg-gray-400'
                                        }`}></div>
                                    </div>
                                )}
                            </div>

                            {/* Span name and service - with proper truncation */}
                            <div className="flex flex-col justify-center min-w-0 overflow-hidden">
                                <div
                                    className={`text-sm ${inSelectedPath ? `font-semibold ${pathStyles.text}` : 'font-medium text-textmedium'} truncate`}>
                                    {node.span.spanName || 'unnamed_span'}
                                </div>
                                {node.span.displayServiceName && node.span.displayServiceName !== 'unknown_service' && (
                                    <div className="text-xs text-textmedium truncate">
                                        {node.span.displayServiceName}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Span bar - adjust position based on new left bar width */}
                    <div
                        className={`absolute h-5 ${statusColor} ${isMatching ? 'opacity-100 ring-2 ring-yellow-400' : isSelected ? 'opacity-100 ring-1 ring-white' : 'opacity-80'} rounded-sm border ${statusBorder} flex items-center px-1 cursor-pointer hover:opacity-100 shadow-sm`}
                        style={{
                            left: `${spanPosition.left}px`,
                            width: `${spanPosition.width}px`
                        }}
                        onMouseEnter={() => setHoveredSpan(node)}
                        onMouseLeave={() => setHoveredSpan(null)}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsViewingTraceLogs(false);
                            setSelectedSpan(node);
                            setShowDetailPanel(true);
                        }}
                        onDoubleClick={(e) => {
                            e.stopPropagation();
                            zoomToSpan(node);
                        }}
                        title="Double-click to zoom to this span"
                    >
            <span className="text-xs text-white truncate font-medium flex-1">
              {formatDuration(node.span.duration)}
            </span>
                        {node.span.links && node.span.links.length > 0 && (
                            <button
                                className="ml-1 p-0.5 hover:text-white flex-shrink-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Open the first linked trace in a new tab
                                    const firstLink = node.span.links![0];
                                    const url = new URL(window.location.origin + "/traces");
                                    
                                    // Use the span's time as reference: 30 minutes before and after
                                    const spanTime = node.span.time; // This is already in milliseconds
                                    const thirtyMinutesMs = 30 * 60 * 1000;
                                    const startTime = (spanTime - thirtyMinutesMs).toString();
                                    const endTime = (spanTime + thirtyMinutesMs).toString();

                                    // Create a filter object with only traceId and spanId
                                    const linkedFilter = {
                                        traceId: [firstLink.traceId],
                                        spanId: [firstLink.spanId]
                                    };

                                    url.searchParams.set("filter", JSON.stringify(linkedFilter));
                                    url.searchParams.set("traceId", firstLink.traceId);
                                    url.searchParams.set("spanId", firstLink.spanId);
                                    url.searchParams.set("startTime", startTime);
                                    url.searchParams.set("endTime", endTime);
                                    url.searchParams.set("startEnd", `${startTime},${endTime}`);

                                    window.open(url.toString(), '_blank');
                                }}
                                title={`Open linked trace${node.span.links.length > 1 ? ` (${node.span.links.length} links)` : ''}`}
                            >
                                <Link className="h-4 w-4 font-semibold text-white hover:text-black"/>
                            </button>
                        )}
                    </div>
                </div>

                {/* Render children with connecting lines */}
                {childrenVisible && node.children.length > 0 && (
                    <div className="relative">
                        {/* Child nodes with individual connection lines */}
                        <div className="flex flex-col">
                            {node.children
                                .filter(child => isNodeOrDescendantMatching(child))
                                .map((childNode, index, filteredChildren) => {
                                const isLastChild = index === filteredChildren.length - 1;
                                const childInSelectedPath = isInPathToSelected(childNode);
                                const childPathStyles = getPathStyles(childNode);

                                // Calculate the vertical line height for this child
                                let verticalLineHeight = 36; // Basic height for the row

                                let childrenLineHeight = isLastChild ? 0 : getChildrenHeight(node.children[index]);

                                return (
                                    <div key={childNode.span.spanId} className="relative">
                                        {/* Vertical line from parent to this child and potentially beyond */}
                                        {index < node.children.length && (
                                            <div
                                                className={`absolute w-px ${
                                                    // Only highlight if this child or any later siblings are in the path
                                                    node.children.slice(index).some(sibling => isInPathToSelected(sibling))
                                                        ? pathStyles.lineBg
                                                        : 'bg-gray-400'
                                                } z-0`}
                                                style={{
                                                    left: `${node.level * 20 + 7.5}px`,
                                                    top: -19,
                                                    height: `${verticalLineHeight + 2}px`
                                                }}
                                            ></div>
                                        )}

                                        {/* Vertical line from parent down to all the children */}
                                        {childrenLineHeight > 0 && (
                                            <div
                                                className={`absolute vlinedown w-px ${
                                                    // If any of the children after this one are in the path, color the line appropriately
                                                    node.children.slice(index + 1).some(sibling => isInPathToSelected(sibling))
                                                        ? pathStyles.lineBg
                                                        : 'bg-gray-400'
                                                } z-0`}
                                                style={{
                                                    left: `${node.level * 20 + 7.5}px`,
                                                    top: 17,
                                                    height: `${childrenLineHeight}px`
                                                }}
                                            ></div>
                                        )}


                                        {/* Horizontal line to child */}
                                        <div
                                            className={`absolute w-4 h-px ${
                                                childInSelectedPath ? childPathStyles.lineBg : 'bg-gray-400'
                                            }`}
                                            style={{
                                                left: `${node.level * 20 + 7.5}px`,
                                                top: 18,
                                            }}
                                        ></div>

                                        {renderSpanTree(childNode, isLastChild)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render the span details panel
    const renderSpanDetailsPanel = () => {
        if (!selectedSpan || !showDetailPanel) return null;

        // Get status information
        let statusColor = 'bg-blue-500';
        let statusText = 'Unset';

        if (selectedSpan.span.statusCode === 'STATUS_CODE_ERROR') {
            statusColor = 'bg-red-500';
            statusText = 'Error';
        } else if (selectedSpan.span.statusCode === 'STATUS_CODE_OK') {
            statusColor = 'bg-green-500';
            statusText = 'OK';
        }

        // Create time information
        const duration = formatDuration(selectedSpan.span.duration);
        const startTime = formatTime(selectedSpan.startTime);
        const offset = formatRelativeTime(selectedSpan.startTime);

        // Combine all attributes into a single object
        const allAttributes: Record<string, string> = {};

        // Extract HTTP-specific attributes
        if (selectedSpan.span.spanAttributes) {
            const attrs = selectedSpan.span.spanAttributes as Record<string, any>;

            // Add all other span attributes with their original keys
            Object.entries(attrs).forEach(([key, value]) => {
                allAttributes[key] = value as string;
            });
        }

        // Add resource attributes
        if (selectedSpan.span.resourceAttributes) {
            const resourceAttrs = selectedSpan.span.resourceAttributes as Record<string, any>;
            Object.entries(resourceAttrs).forEach(([key, value]) => {
                allAttributes[key] = value as string;
            });
        }

        // Get service info
        const serviceInfo = selectedSpan.span.displayServiceName &&
        selectedSpan.span.displayServiceName !== 'unknown_service' ?
            selectedSpan.span.displayServiceName : '';

        return (
            <div className="h-full bg-backgrounddark flex flex-col overflow-hidden">
                {/* Header */}
                <div
                    className="flex justify-between items-center px-4 py-2 border-b border-backgroundlight sticky top-0 bg-backgrounddark z-10 flex-shrink-0">
                    <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${statusColor}`}></div>
                        <h3 className="text-base font-medium flex items-center gap-2 text-textmedium">
                            <span
                                className="truncate max-w-[300px]">{selectedSpan.span.spanName || 'unnamed_span'}</span>
                            {serviceInfo && (
                                <span className="text-sm text-textmedium">({serviceInfo})</span>
                            )}
                        </h3>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => zoomToSpan(selectedSpan)}
                            className="text-textmedium hover:text-primary rounded-full hover:bg-backgroundlight p-1"
                            title="Zoom to this span and its children"
                        >
                            <ZoomInIcon size={16}/>
                        </button>
                        <button
                            onClick={() => {
                                setShowDetailPanel(false);
                            }}
                            className="text-textmedium hover:text-primary rounded-full hover:bg-backgroundlight p-1"
                        >
                            <XIcon size={16}/>
                        </button>
                    </div>
                </div>
                
                {/* Tabs */}
                <div className="flex border-b border-backgroundlight">
                    <button
                        onClick={() => {
                            setActiveTab('details');
                            setIsViewingTraceLogs(false);
                        }}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'details'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-textmedium hover:text-textlight'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <TagIcon size={14} />
                            Details
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('logs')}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'logs'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-textmedium hover:text-textlight'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <FileTextIcon size={14} />
                            Logs
                        </div>
                    </button>
                </div>

                {/* Tab Content */}
                <div className="flex-grow overflow-hidden">
                    {activeTab === 'details' ? (
                        <div className="p-3 overflow-y-auto h-full text-textmedium">
                            {/* Time & Duration section - separate from tags */}
                            <div className="mb-4 p-2">
                        <div className="grid grid-cols-3 gap-2">
                            <div className="flex flex-col">
                                <span className="text-xs text-textmedium/70">Duration</span>
                                <span className="text-sm font-medium">{duration}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-textmedium/70">Start Time</span>
                                <span className="text-sm font-medium">{startTime}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-textmedium/70">Offset from start of trace</span>
                                <span className="text-sm font-medium">{offset}</span>
                            </div>
                        </div>
                    </div>

                    {/* Links section */}
                    {selectedSpan.span.links && selectedSpan.span.links.length > 0 && (
                        <div className="mb-4">
                            <div className={"bg-primarytransparent border rounded border-primary transition-colors"}>
                                <div className="flex items-center gap-2 p-2">
                                    <Link className="h-4 w-4 text-textmedium"/>
                                    <h4 className="text-sm font-semibold text-textmedium ">Linked Traces</h4>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {selectedSpan.span.links.map((link: LinkType, index: number) => (
                                        <div key={index} className="flex items-center gap-2 p-2">
                                            <div className="flex-1">
                                                <div className="text-sm">
                                                    <span className="text-textmedium">Trace ID: </span>
                                                    <span className="font-mono text-textmedium">{link.traceId}</span>
                                                </div>
                                                {link.spanId && (
                                                    <div className="text-xs text-textmedium">
                                                        <span>Span ID: </span>
                                                        <span className="font-mono">{link.spanId}</span>
                                                    </div>
                                                )}
                                                {link.attributes && Object.keys(link.attributes).length > 0 && (
                                                    <div className="text-xs text-textmedium mt-1">
                                                        {Object.entries(link.attributes).map(([key, value]) => (
                                                            <span key={key} className="mr-3">
                              {key}: <span className="font-medium">{value}</span>
                            </span>))}</div>)}</div>
                                            <button
                                                onClick={() => {
                                                    const url = new URL(window.location.origin + "/traces");
                                                    
                                                    // Use the selected span's time as reference: 30 minutes before and after
                                                    const spanTime = selectedSpan.span.time; // This is already in milliseconds
                                                    const thirtyMinutesMs = 30 * 60 * 1000;
                                                    const startTime = (spanTime - thirtyMinutesMs).toString();
                                                    const endTime = (spanTime + thirtyMinutesMs).toString();

                                                    // Create a filter object with only traceId and spanId
                                                    const linkedFilter = {
                                                        traceId: [link.traceId],
                                                        spanId: [link.spanId]
                                                    };

                                                    url.searchParams.set("filter", JSON.stringify(linkedFilter));
                                                    url.searchParams.set("traceId", link.traceId);
                                                    url.searchParams.set("spanId", link.spanId || '');
                                                    url.searchParams.set("startTime", startTime);
                                                    url.searchParams.set("endTime", endTime);
                                                    url.searchParams.set("startEnd", `${startTime},${endTime}`);

                                                    window.open(url.toString(), '_blank');
                                                }}
                                                className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded transition-colors"
                                                title="Open linked trace in new tab"
                                            >
                                                <ExternalLinkIcon className="h-4 w-4"/>
                                                <span className={"text-base"}>Open</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* All attributes in a single tag container */}
                    <TagContainer
                        title="Attributes"
                        attributes={allAttributes}
                        icon={<TagIcon className="text-textmedium" size={14}/>}
                        setWidth="w-full"
                        filter={filter}
                        setFilter={setFilter}
                        excludeFilter={excludeFilter}
                        setExcludeFilter={setExcludeFilter}
                    />
                        </div>
                    ) : (
                        <div className="h-full overflow-hidden flex flex-col">
                            <div className="flex-grow overflow-hidden flex mt-2">
                                <LogsComponent
                                    justLogsFeed={true}
                                    noBaseView={true}
                                    filter={logsFilter}
                                    setFilter={setLogsFilter}
                                    excludeFilter={logsExcludeFilter}
                                    setExcludeFilter={setLogsExcludeFilter}
                                    regexes={logsRegexes}
                                    setRegexes={setLogsRegexes}
                                    excludeRegexes={logsExcludeRegexes}
                                    setExcludeRegexes={setLogsExcludeRegexes}
                                    ascending={logsAscending}
                                    setAscending={setLogsAscending}
                                    setFreeTextFilter={() => {}}
                                    transforms={logsTransforms}
                                    setTransforms={setLogsTransforms}
                                    customColumns={logsCustomColumns}
                                    setCustomColumns={setLogsCustomColumns}
                                    hideSearchBar={false}
                                    showHistogram={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Handle mouse down for panning the timeline
    const handleTimelinePanStart = (e: React.MouseEvent) => {
        // Only start panning if ctrl or cmd key is pressed
        if (e.ctrlKey || e.metaKey) {
            setIsPanning(true);
            lastPanPosRef.current = e.clientX;
            e.preventDefault();

            // Change cursor to indicate panning is active
            if (containerRef.current) {
                containerRef.current.style.cursor = 'grabbing';
            }
        }
    };

    // Handle mouse move for panning
    useEffect(() => {
        if (!isPanning) return;

        const handlePanMove = (e: globalThis.MouseEvent) => {
            const delta = lastPanPosRef.current - e.clientX;
            lastPanPosRef.current = e.clientX;

            // Calculate how much to move the timeline
            const timeRange = timelineRange.end - timelineRange.start;
            const timelineWidth = Math.max(containerWidth - directoryWidth, 50);
            const timeShift = (delta / timelineWidth) * timeRange;

            // Don't allow panning beyond the original time range
            if (timelineRange.start + timeShift < originalTimeRange.start) {
                setTimelineRange({
                    start: originalTimeRange.start,
                    end: originalTimeRange.start + timeRange
                });
                return;
            }

            if (timelineRange.end + timeShift > originalTimeRange.end) {
                setTimelineRange({
                    start: originalTimeRange.end - timeRange,
                    end: originalTimeRange.end
                });
                return;
            }

            // Apply the pan
            setTimelineRange({
                start: timelineRange.start + timeShift,
                end: timelineRange.end + timeShift
            });
        };

        const handlePanEnd = () => {
            setIsPanning(false);
            if (containerRef.current) {
                containerRef.current.style.cursor = '';
            }
        };

        document.addEventListener('mousemove', handlePanMove);
        document.addEventListener('mouseup', handlePanEnd);

        return () => {
            document.removeEventListener('mousemove', handlePanMove);
            document.removeEventListener('mouseup', handlePanEnd);
        };
    }, [isPanning, timelineRange, containerWidth, directoryWidth, originalTimeRange]);

    // Handle wheel events for zooming with cmd/ctrl + scroll
    const handleWheel = (e: React.WheelEvent) => {
        // Only handle zooming if ctrl or cmd key is pressed
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();

            // Calculate zoom center based on mouse position
            const rect = e.currentTarget.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;

            // Skip if mouse is in the directory area
            if (mouseX <= directoryWidth) return;

            // Calculate the time at mouse position
            const timelineWidth = containerWidth - directoryWidth;
            const mouseTimePosition = timelineRange.start +
                ((mouseX - directoryWidth) / timelineWidth) * (timelineRange.end - timelineRange.start);

            // Calculate zoom factor - use smaller increments for smoother zooming
            // Scale the factor based on the delta to make it feel responsive but not too fast
            const zoomDelta = Math.min(Math.abs(e.deltaY), 50) / 200; // Cap and normalize the delta
            const zoomFactor = e.deltaY < 0
                ? (1 - zoomDelta) // Zoom in: between 0.85 and 0.95 depending on scroll speed
                : (1 + zoomDelta); // Zoom out: between 1.05 and 1.15 depending on scroll speed

            // Don't zoom out beyond a reasonable limit - but only apply this when zooming out
            // and when we're not already more zoomed in than the original range
            if (zoomFactor > 1) { // Zooming out
                const currentRange = timelineRange.end - timelineRange.start;
                const newRange = currentRange * zoomFactor;
                const originalRange = originalTimeRange.end - originalTimeRange.start;

                // Only limit zoom out if we're already at a reasonable zoom level
                // If we're zoomed in too much, allow zooming out regardless of limit
                if (newRange > originalRange * 1.5 && currentRange >= originalRange * 0.5) {
                    return;
                }
            }

            // Calculate new time range, keeping the mouse position stable
            const currentRange = timelineRange.end - timelineRange.start;
            const newRange = currentRange * zoomFactor;
            const rangeChange = newRange - currentRange;

            // Calculate how much the start and end times should change
            // This ensures the time under the mouse cursor stays fixed
            const mouseOffset = (mouseTimePosition - timelineRange.start) / currentRange;
            const startDelta = rangeChange * mouseOffset;
            const endDelta = rangeChange * (1 - mouseOffset);

            // Apply the new time range
            const newStart = Math.max(timelineRange.start - startDelta, originalTimeRange.start);
            const newEnd = Math.min(timelineRange.end + endDelta, originalTimeRange.end);

            // Ensure we're not zooming in to an impossibly small range
            if (newEnd - newStart < 0.001) {
                return;
            }

            setTimelineRange({
                start: newStart,
                end: newEnd
            });

            // Update zoom level for UI purposes - use a smaller increment
            const zoomLevelMultiplier = e.deltaY < 0 ? 1.05 : 0.95;
            setZoomLevel(zoomLevel * zoomLevelMultiplier);
        }
    };

    // Add a className to show cursor when ctrl or cmd is pressed
    const [isCtrlPressed, setIsCtrlPressed] = useState<boolean>(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey) {
                setIsCtrlPressed(true);
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (!e.ctrlKey && !e.metaKey) {
                setIsCtrlPressed(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    // Add effect to force layout update when spans or timeline range changes
    useEffect(() => {
        if (!containerRef.current || !timelineRange.start || !timelineRange.end) return;

        // Force a recalculation of dimensions and layout
        const updateLayout = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
                setContainerHeight(containerRef.current.offsetHeight);
            }
        };

        // Update immediately and after a short delay to handle any re-renders
        updateLayout();
        const timeoutId = setTimeout(updateLayout, 50);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [spans, timelineRange, spanTree]);

    if (!spanTree || spans.length === 0) {
        return <div className="p-4 text-textmedium">No spans to display</div>;
    }

    // Check if any spans have links
    const hasSpansWithLinks = spans.some(span => span.links && span.links.length > 0);

    // Display error message for missing parent span
    const renderMissingParentSpanMessage = () => {
        if (!missingParentSpanId) return null;

        return (
            <div className="w-full px-4 py-2 bg-red-500/10 rounded border border-red-500/30 flex">
                <InfoIcon className="h-5 w-5 text-red-500 flex-shrink-0"/>
                <div className="text-sm text-textmedium ml-2">
                    The trace has a missing span with ID: <span className="font-semibold">{missingParentSpanId}</span>.
                    As a result, the data shown below may be incomplete or inaccurate.
                </div>
            </div>
        );
    };

    // Display message about linked traces
    const renderLinkedTracesMessage = () => {
        if (!hasSpansWithLinks) return null;

        return (
            <div className="w-full px-4 py-2 rounded border border-border flex">
                <Link className="h-5 w-5 text-primary flex-shrink-0"/>
                <div className="text-sm text-textmedium ml-2">
                    <span className="font-medium">This trace contains linked traces.</span> Click on the <Link className="inline h-4 w-4 mx-1 text-primary"/> icon on span bars to open linked traces, or click on a span bar to see link details.
                </div>
            </div>
        );
    };

    return (
        <div
            className="flex flex-col h-full overflow-hidden relative gap-2"
            onWheel={handleWheel}
        >
            {/* Display error message for missing parent span */}
            {renderMissingParentSpanMessage()}

            {/* Display message about linked traces */}
            {renderLinkedTracesMessage()}

            {/* Search bar */}
            <div className="w-full bg-backgrounddark border-b border-backgroundlight flex items-center gap-2 p-2">
                <div className="relative flex-1 max-w-md">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-textmedium" />
                    <input
                        type="text"
                        placeholder="Search spans by ID or attribute value..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 bg-backgroundmedium border border-border rounded text-sm text-textmedium placeholder-textmedium/50 focus:outline-none focus:border-primary"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-textmedium hover:text-primary"
                        >
                            <XIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
                {isSearchActive && (
                    <div className="text-sm text-textmedium">
                        <span className="font-medium">{matchingSpanIds.size}</span> matching span{matchingSpanIds.size !== 1 ? 's' : ''}
                    </div>
                )}
            </div>

            <div
                className="flex flex-col flex-grow overflow-hidden transition-all duration-200"
                style={{height: showDetailPanel ? `calc(100% - ${detailsHeight}px)` : '100%'}}
                ref={containerRef}
            >
                {/* Timeline header with zoom controls */}
                <div className="relative flex border-b border-backgroundlight flex-shrink-0">
                    {/* Directory header - fixed width */}
                    <div
                        className="flex-shrink-0 h-8 flex items-center px-4 font-medium text-xs text-textmedium bg-backgroundlight/30"
                        style={{width: `${directoryWidth}px`}}
                    >
                        <span>Span Name</span>
                    </div>

                    {/* Resize handle - better positioning */}
                    <div
                        className={`w-1 h-8 cursor-col-resize hover:bg-primary hover:opacity-100 ${isResizing ? 'bg-primary opacity-100' : 'bg-gray-400 opacity-50'} z-10`}
                        onMouseDown={handleResizeStart}
                    ></div>

                    {/* Timeline - expands to fill remaining space */}
                    <div
                        className={`flex-1 pt-2 relative ${isCtrlPressed ? 'cursor-grab' : ''} ${isPanning ? 'cursor-grabbing' : ''}`}
                        onMouseDown={handleTimelinePanStart}
                    >
                        {renderTimelineMarkers()}
                        <div className="absolute -top-1 -right-0 flex space-x-1">
                            <button
                                onClick={handleZoomIn}
                                className="p-1 text-textmedium hover:text-primary rounded hover:bg-backgroundlight"
                                title="Zoom in"
                            >
                                <ZoomInIcon size={14}/>
                            </button>
                            <button
                                onClick={handleZoomOut}
                                className="p-1 text-textmedium hover:text-primary rounded hover:bg-backgroundlight"
                                title="Zoom out"
                            >
                                <ZoomOutIcon size={14}/>
                            </button>
                            <button
                                onClick={handleResetZoom}
                                className="p-1 text-textmedium hover:text-primary rounded hover:bg-backgroundlight"
                                title="Reset zoom"
                            >
                                <RefreshCwIcon size={14}/>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Spans container - This is the only element that should scroll */}
                <div
                    className={`flex-grow overflow-y-auto overflow-x-hidden ${isCtrlPressed ? 'cursor-grab' : ''} ${isPanning ? 'cursor-grabbing' : ''} relative`}
                    onMouseDown={handleTimelinePanStart}
                >
                    {/* Keyboard shortcut tooltip overlay */}
                    {showKeyboardShortcuts && (
                        <div
                            className="absolute right-6 top-6 text-xs bg-backgrounddark px-3 py-2 rounded z-50 shadow-md border border-backgroundlight text-textmedium">
                            <button
                                onClick={() => setShowKeyboardShortcuts(false)}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-backgrounddark border border-backgroundlight rounded-full flex items-center justify-center hover:bg-backgroundlight transition-colors"
                                title="Close"
                            >
                                <XIcon className="h-3 w-3 text-textmedium" />
                            </button>
                            <div className="flex flex-col space-y-1">
                                <div>
                                    <kbd
                                        className="px-1 py-0.5 text-[10px] font-semibold border border-backgroundlight rounded bg-backgrounddark">
                                        {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}
                                    </kbd>
                                    <span className="mx-1 text-textmedium">+</span>
                                    <span className="text-textmedium">scroll to zoom</span>
                                </div>
                                <div>
                                    <span className="text-textmedium">Double-click span to zoom to it</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {renderSpanTree(spanTree)}
                </div>
            </div>

            {/* Details panel at the bottom with transition */}
            {showDetailPanel && (
                <>
                    {/* Resize handle for details panel */}
                    <div
                        className={`h-1 cursor-row-resize hover:bg-primary hover:opacity-100 ${isResizingDetails ? 'bg-primary opacity-100' : 'bg-gray-400 opacity-50'} z-10`}
                        onMouseDown={handleDetailsResizeStart}
                    ></div>

                    <div
                        className="transition-all duration-200 border-t border-backgroundlight overflow-hidden flex-shrink-0"
                        style={{height: `${detailsHeight}px`}}
                    >
                        {renderSpanDetailsPanel()}
                    </div>
                </>
            )}
        </div>
    );
});
